import { useState, useMemo, useCallback, useEffect } from "react";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { View, Text } from "react-native";
import { FlashList } from "@shopify/flash-list";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { BackButton } from "@/shared/components/ui/back-button";
import { Heading } from "@/shared/components/ui/heading";
import { ItemSeparator } from "@/shared/components/ui/item-separator";
import { SearchInput } from "@/shared/components/ui/search-input";
import { UnderlineTabs } from "@/shared/components/ui/underline-tabs";
import { useEventParticipants } from "@/features/events/api/event-participants";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { UserListItem } from "@/features/profile/components/user-list-item";
import type { EventParticipantDetails } from "@lezz/shared";
import {
  EVENT_PARTICIPANT_CONFIRMED,
  EVENT_PARTICIPANT_INTERESTED,
} from "@/features/events/constants";

const TABS = [
  {
    key: EVENT_PARTICIPANT_CONFIRMED,
    label: "Confirmados",
    icon: "checkmark-outline" as const,
  },
  {
    key: EVENT_PARTICIPANT_INTERESTED,
    label: "Interesse",
    icon: "heart-outline" as const,
  },
] as const;

export default function EventParticipantsScreen() {
  const { eventId } = useLocalSearchParams<{ eventId: string }>();
  const { isLoggedIn } = useAuthContext();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  // "Who's going" is login-only — protect direct-URL access to this route.
  useEffect(() => {
    if (!isLoggedIn) {
      const redirect = encodeURIComponent(`/event/${eventId}/participants`);
      router.replace(`/login?redirect=${redirect}`);
    }
  }, [isLoggedIn, eventId, router]);

  const [activeTab, setActiveTab] = useState<string>(
    EVENT_PARTICIPANT_CONFIRMED
  );
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: participants,
    isLoading: isLoadingParticipants,
    error: participantsError,
  } = useEventParticipants(eventId);

  const filteredParticipants = useMemo(() => {
    const byTab = participants?.filter((p) => p.participantType === activeTab);
    if (!searchQuery.trim()) return byTab;
    const lower = searchQuery.toLowerCase();
    return byTab?.filter(
      (p) =>
        p.profileDisplayName?.toLowerCase().includes(lower) ||
        p.profileUsername?.toLowerCase().includes(lower)
    );
  }, [participants, activeTab, searchQuery]);

  const renderParticipant = useCallback(
    ({ item }: { item: EventParticipantDetails }) => (
      <UserListItem
        userId={item.userId ?? ""}
        name={item.profileDisplayName || item.profileUsername || "Unknown"}
        username={item.profileUsername ?? ""}
        avatarUrl={item.profileAvatarUrl}
        avatarSize={56}
      />
    ),
    []
  );

  if (!isLoggedIn) return null;

  return (
    <>
      <Stack.Screen options={{ title: "Participantes", headerShown: false }} />
      <ScreenContainer style={{ paddingTop: 0 }}>
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-4 pb-4"
          style={{ paddingTop: insets.top || 24 }}
        >
          <BackButton />
          <Heading variant="section" accessibilityRole="header">
            Participantes
          </Heading>
          <View className="h-10 w-10" />
        </View>

        <UnderlineTabs
          items={TABS}
          activeKey={activeTab}
          onChange={setActiveTab}
        />

        <View className="px-5 pt-4 pb-2">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Pesquisar"
          />
        </View>

        <FlashList
          data={isLoadingParticipants ? [] : (filteredParticipants ?? [])}
          keyExtractor={(item, index) =>
            item.userId ?? item.profileId ?? String(index)
          }
          renderItem={renderParticipant}
          contentInsetAdjustmentBehavior="automatic"
          ListEmptyComponent={
            isLoadingParticipants ? null : participantsError ? (
              <Text
                className="text-destructive px-5 pt-4 text-sm italic"
                accessibilityRole="alert"
              >
                Failed to load participants
              </Text>
            ) : (
              <Text className="text-muted-foreground px-5 pt-4 text-sm italic">
                Sem participantes ainda. Seja a primeira a participar!
              </Text>
            )
          }
          ItemSeparatorComponent={ItemSeparator}
          contentContainerStyle={{ padding: 20 }}
          showsVerticalScrollIndicator={false}
        />
      </ScreenContainer>
    </>
  );
}
